import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Dialog, DialogContent, IconButton, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { nanoid } from 'nanoid';
import { API } from '../../../../../../../../api';
import { useAppDispatch, useAppSelector } from '../../../../../../../../redux/hooks';
import { fetchAllArticles } from '../../../../../../../../redux/slices/article/getAllArticleSlice';
import { LessonMaterial } from '../../../../../../../../components/admin3/newProgram/masterclass/types';
import CommonModalHeader from '../../../../../../../../components/DialogUi/CommonModalHeader';
import DosenButton from '../../../../../../../../components/ui/Buttons/DosenButton';
import dosenFavicon from '../../../../../../../../assets/images/brand/dosen/favicon.png';
import ContentsModal from '../../settings components/ContentsModal';
import AddEditContent from '../../../../../Help/components/AddEditContent';

const sectionBoxSx = {
  border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
  borderRadius: '12px',
  background: 'rgba(255,255,255,0.02)',
  p: 2.5
} as const;

interface LessonResourcesSectionProps {
  grpId: string;
  orgId: string;
  location: string;
  materials: LessonMaterial[];
  onChange: (materials: LessonMaterial[]) => void;
}

/**
 * Lesson resources editor for the post-creation design builder. Mirrors the manual
 * builder's resources flow (select existing library article via ContentsModal, or
 * create/edit via AddEditContent), operating on `LessonMaterial[]`. Titles are
 * hydrated from the org article store. Changes are held in the builder's lesson
 * state and persist on "Save Changes" (materials → libraryArticles).
 */
const LessonResourcesSection: React.FC<LessonResourcesSectionProps> = ({
  grpId,
  orgId,
  location,
  materials,
  onChange
}) => {
  const dispatch = useAppDispatch();
  const articleList = useAppSelector((s) => s.allArticle.data?.ArticleList || []);
  // Default card art for resources without their own cover: the org logo.
  const orgLogo = useAppSelector((s) => s.orgDetails.data?.orgLogo);

  // Populate the org article store (for title hydration + the "select existing" picker).
  //
  // `allArticle` is a GLOBAL store shared with every other screen that lists
  // articles. It used to be skipped whenever it already held something
  // (`articleList.length > 0`), so arriving here from the org Resources page or
  // another program hydrated the cards against that other list. Group-scoped
  // resources ("Program only") are absent from any list fetched under a
  // different group, so their cards silently fell back to "Resource" + the
  // Dosen favicon. Always refetch for the program in context.
  const [listSettledForGrp, setListSettledForGrp] = useState<string | null>(null);
  /** Ids already sent to the by-id fallback below — never retried on loop. */
  const attemptedIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!orgId || !grpId) return undefined;
    let cancelled = false;
    setListSettledForGrp(null);
    // A new program gets a fresh set of by-id attempts.
    attemptedIds.current = new Set();
    dispatch(fetchAllArticles({ orgId, groupId: grpId, helpSettings: false, useCache: false, role: 'admin' })).finally(
      () => {
        if (!cancelled) setListSettledForGrp(grpId);
      }
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, grpId]);

  const articleMap = useMemo(
    () =>
      articleList.reduce<Record<string, any>>((acc, a: any) => {
        acc[a.articleId] = a;
        return acc;
      }, {}),
    [articleList]
  );

  // Articles resolved one-by-one because the group-scoped list did not carry
  // them. The by-id endpoint applies no group/visibility filter, so it is the
  // authoritative fallback — the same one the edit modal has always used, which
  // is why clicking a broken card showed the article perfectly.
  const [fetchedById, setFetchedById] = useState<Record<string, any>>({});

  const hydrated = useMemo(
    () =>
      materials.map((m): LessonMaterial => {
        const id = m.articleId || m.id;
        const a = id ? articleMap[id] || fetchedById[id] : undefined;
        return {
          ...m,
          title: m.title || a?.articleName || a?.title || 'Resource',
          thumbnail: m.thumbnail || a?.coverImageUrl
        };
      }),
    [materials, articleMap, fetchedById]
  );

  const [choiceOpen, setChoiceOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [addEdit, setAddEdit] = useState<{
    open: boolean;
    action: 'Add' | 'Edit';
    articleId?: string;
    articleData?: any;
    materialId?: string;
  }>({ open: false, action: 'Add' });

  const currentSelection = useMemo(
    () => materials.map((m) => m.articleId || m.id).filter(Boolean) as string[],
    [materials]
  );

  const handleSelectExisting = (articleIds: string[]) => {
    const selected = articleIds
      .map((articleId): LessonMaterial | null => {
        const a: any = articleList.find((item: any) => item.articleId === articleId);
        if (!a) return null;
        return {
          id: articleId,
          articleId,
          title: a.articleName || a.title || a.name || 'Resource',
          reference: a.articleUrl || a.articleLink || a.fileUrl || '',
          thumbnail: a.coverImageUrl || a.coverImg || '',
          readTime: a.readTime,
          source: 'library'
        };
      })
      .filter(Boolean) as LessonMaterial[];
    // Replace the library-sourced set (matches the manual builder), keep any custom ones.
    onChange([...materials.filter((m) => (m.source || 'custom') !== 'library'), ...selected]);
    setPickerOpen(false);
  };

  const loadArticleDetails = useCallback(
    async (articleId?: string) => {
      if (!articleId) return null;
      try {
        const res = await API.getAllArticleById({
          orgId: orgId ?? '',
          articleId,
          groupId: grpId,
          isContentSettings: true,
          articleLocation: location,
          useCache: false
        });
        return res?.data?.article || null;
      } catch {
        return null;
      }
    },
    [orgId, grpId, location]
  );

  // Resolve anything the list did not cover. Waits for our own fetch to settle
  // so a cold mount does not fire one request per card.
  useEffect(() => {
    if (listSettledForGrp !== grpId) return;
    const missing = materials
      .map((m) => m.articleId || m.id)
      .filter((id): id is string => Boolean(id) && !articleMap[id] && !attemptedIds.current.has(id));
    if (missing.length === 0) return;
    missing.forEach((id) => attemptedIds.current.add(id));
    Promise.all(missing.map((id) => loadArticleDetails(id).then((article) => [id, article] as const))).then(
      (results) => {
        const resolved = results.filter(([, article]) => Boolean(article));
        if (resolved.length === 0) return;
        setFetchedById((prev) => ({ ...prev, ...Object.fromEntries(resolved) }));
      }
    );
  }, [listSettledForGrp, grpId, materials, articleMap, loadArticleDetails]);

  const openEditor = async (material: LessonMaterial) => {
    const articleId = material.articleId || material.id;
    const articleData = articleId
      ? articleMap[articleId] || fetchedById[articleId] || (await loadArticleDetails(articleId))
      : undefined;
    setAddEdit({ open: true, action: 'Edit', articleId, articleData, materialId: material.id });
  };

  const handleArticleSaved = (article: any) => {
    const newArticle = Array.isArray(article) ? article[0] : article;
    setAddEdit((prev) => ({ ...prev, open: false }));
    if (!newArticle) return;
    const articleId = newArticle.articleId || newArticle._id || nanoid();
    const next: LessonMaterial = {
      id: articleId,
      articleId,
      title: newArticle.articleName || newArticle.title || newArticle.name || 'Resource',
      reference: newArticle.articleUrl || newArticle.articleLink || newArticle.fileUrl || '',
      thumbnail: newArticle.coverImageUrl || newArticle.coverImg || '',
      readTime: newArticle.readTime,
      source: 'library'
    };
    if (addEdit.action === 'Edit' && addEdit.materialId) {
      onChange(
        materials.map((m) =>
          m.id === addEdit.materialId
            ? {
                ...m,
                title: next.title,
                reference: next.reference,
                thumbnail: next.thumbnail,
                articleId: next.articleId
              }
            : m
        )
      );
      return;
    }
    onChange([...materials.filter((m) => (m.articleId || m.id) !== next.articleId), next]);
  };

  const remove = (materialId: string) => onChange(materials.filter((m) => m.id !== materialId));

  return (
    <Box sx={sectionBoxSx}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1.5,
          flexWrap: 'wrap'
        }}
      >
        <Box>
          <Typography
            sx={{ fontSize: 15, fontWeight: 600, fontFamily: 'Inter', color: 'var(--Text-text-tertiary, #868686)' }}
          >
            Resources
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              fontFamily: 'Inter',
              color: 'var(--Text-text-secondary, #B6B6B6)',
              lineHeight: 1.5,
              mt: 0.5
            }}
          >
            Resources the learner can open from this lesson.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setChoiceOpen(true)}
          sx={{
            textTransform: 'none',
            color: '#FDFDFD',
            borderColor: '#2A2A2A',
            '&:hover': { borderColor: '#F79420' }
          }}
        >
          Add Resource
        </Button>
      </Box>

      {hydrated.length === 0 ? (
        <Typography
          sx={{ fontSize: 12, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)', fontStyle: 'italic' }}
        >
          No resources for this lesson yet.
        </Typography>
      ) : (
        <Box display="flex" flexWrap="wrap" gap={2}>
          {hydrated.map((material) => (
            <Box
              key={material.id}
              onClick={() => openEditor(material)}
              sx={{ width: 150, display: 'flex', flexDirection: 'column', gap: 1, cursor: 'pointer' }}
            >
              <Box
                component="img"
                src={material.thumbnail || orgLogo || dosenFavicon}
                alt={material.title || 'Resource'}
                onError={(e: any) => {
                  e.target.src = orgLogo || dosenFavicon;
                }}
                sx={{ width: '100%', height: 90, borderRadius: 1, outline: '2px solid #2A2A2A', objectFit: 'cover' }}
              />
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography sx={{ color: '#FDFDFD', fontSize: 12, flex: 1, minWidth: 0 }} noWrap>
                  {material.title || 'Resource'}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    remove(material.id);
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" sx={{ color: '#B6B6B6' }} />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Existing-library picker */}
      <ContentsModal
        openContents={pickerOpen}
        onClose={() => setPickerOpen(false)}
        setArticles={handleSelectExisting}
        articlesSelected={currentSelection}
      />

      {/* Add-resource choice: same surface as the other house modals, with one
          primary (Add) and one secondary choice. */}
      <Dialog
        open={choiceOpen}
        onClose={() => setChoiceOpen(false)}
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: 550,
            bgcolor: '#0F0F0F',
            color: '#FDFDFD',
            borderRadius: 2,
            border: '1px solid #444444'
          }
        }}
      >
        <CommonModalHeader title="Add new resource" handleCloseModal={() => setChoiceOpen(false)} />
        <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 3 }}>
          <Typography color="#B6B6B6" fontSize={14} fontFamily="Inter, sans-serif" sx={{ mb: 2.5 }}>
            How do you want to add a resource?
          </Typography>
          <Box display="flex" gap={1.5}>
            <DosenButton
              tone="basic"
              fullWidth
              onClick={() => {
                setChoiceOpen(false);
                setPickerOpen(true);
              }}
            >
              Select Existing Resource
            </DosenButton>
            <DosenButton
              fullWidth
              onClick={() => {
                setChoiceOpen(false);
                setAddEdit({ open: true, action: 'Add' });
              }}
            >
              Add
            </DosenButton>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Create / edit an article-backed resource */}
      <AddEditContent
        openForm={addEdit.open}
        handleClickCloseForm={() => setAddEdit((prev) => ({ ...prev, open: false }))}
        setOpenForm={(open: boolean) => setAddEdit((prev) => ({ ...prev, open }))}
        selectedActiveId={addEdit.articleId || ''}
        singleListData={addEdit.articleData || {}}
        action={addEdit.action}
        setAction={(action: 'Add' | 'Edit') => setAddEdit((prev) => ({ ...prev, action }))}
        onNewArticleCreated={handleArticleSaved}
        newGroupId={grpId}
      />
    </Box>
  );
};

export default LessonResourcesSection;
